import React, { useState, useMemo } from 'react';
import { Card, Table, Typography, Button, Space, Progress, Input, Tag, Row, Col, Select, Statistic } from 'antd';
import { SortAscendingOutlined, SortDescendingOutlined, SearchOutlined } from '@ant-design/icons';
import useAllRoutes from '../../../hooks/useAllRoutes';
import { keyBy } from 'lodash';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * RoutePerformance Component
 * Displays detailed route performance metrics from Transport for NSW Trip Passing Time Reports
 */
const RoutePerformance = ({ reportData }) => {
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [minTrips, setMinTrips] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(15);

  // Get all routes from the hook
  const { allRoutes } = useAllRoutes();

  // Create a lookup object for routes by routeNumber for faster access
  const allRoutesByNumber = useMemo(() => keyBy(allRoutes || [], 'routeNumber'), [allRoutes]);

  // If no data, show empty state
  if (!reportData || !reportData.routePerformance || !reportData.routePerformance.routes) {
    return (
      <Card title="Route Performance">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No route performance data available. Please generate a report.</Text>
        </div>
      </Card>
    );
  }

  const { routePerformance } = reportData;
  const routes = routePerformance.routes || [];

  // Format percentage values
  const formatPercent = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '0%';
    return `${Math.min(value, 100).toFixed(2)}%`;
  };

  // Enhance routes with additional data from allRoutes
  const enhancedRoutes = routes.map(route => {
    // Find the matching route in allRoutes using routeNumber
    const routeObject = allRoutesByNumber[route.route];
    
    // Check for OTR values - multiple places they could be stored
    // 1. Directly on the route object
    // 2. In a stopPositionData nested object
    // 3. In a byPosition nested object
    // 4. In a stopStats nested object
    
    // Helper function to safely extract value with fallbacks
    const getOtrValue = (path, defaultValue = 0) => {
      try {
        // Try different possible paths where the data might be found
        if (route[path] !== undefined && !isNaN(route[path])) {
          return route[path];
        }
        
        if (route.stopPositionData?.[path]?.onTimePercentage !== undefined) {
          return route.stopPositionData[path].onTimePercentage;
        }
        
        if (route.byPosition?.[path]?.otrPercentage !== undefined) {
          return route.byPosition[path].otrPercentage;
        }
        
        if (route.stopStats?.[path]?.otrPercentage !== undefined) {
          return route.stopStats[path].otrPercentage;
        }
        
        // Handle array of stop positions
        if (route.stopPositions) {
          const position = route.stopPositions.find(p => 
            p.position?.toLowerCase() === path.toLowerCase() || 
            p.stopType?.toLowerCase() === path.toLowerCase()
          );
          if (position?.otrPercentage !== undefined) {
            return position.otrPercentage;
          }
        }
        
        // Fall back to default value if nothing found
        return defaultValue;
      } catch (err) {
        console.warn(`Error extracting ${path} OTR value:`, err);
        return defaultValue;
      }
    };
    
    // Extract OTR values using the helper function
    const firstStopOTR = getOtrValue('first', route.firstStopOTR || 0);
    const middleStopOTR = getOtrValue('middle', route.middleStopOTR || 0);
    const lastStopOTR = getOtrValue('last', route.lastStopOTR || 0);
    
    // For debugging
    if (firstStopOTR === 0 && middleStopOTR === 0 && lastStopOTR === 0) {
      console.debug('No position OTR data found for route:', route.route, route);
    }
    
    return {
      ...route,
      // Add additional information from the matched route
      routeName: routeObject ? `${routeObject?.routeNumber}:${routeObject?.routeName}` : route.route,
      routeType: routeObject?.type,
      routeColor: routeObject?.color,
      // Keep the original route as the reference
      route: route.route,
      // Add or update the OTR values
      firstStopOTR,
      middleStopOTR,
      lastStopOTR,
      routeObject: routeObject,
      firstStops: { total: 0, onTime: 0 },
      middleStops: { total: 0, onTime: 0 },
      lastStops: { total: 0, onTime: 0 }
    };
  });

  // Filter and sort routes
  const filteredRoutes = enhancedRoutes
    .filter(route => {
      // Apply search filter - check both route number and name
      if (searchTerm && 
          !route.route.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !(route.routeName && route.routeName.toLowerCase().includes(searchTerm.toLowerCase()))) {
        return false;
      }
      
      // Apply min trips filter
      return route.totalTrips >= minTrips;
    })
    .sort((a, b) => {
      // Sort by OTR percentage
      return sortOrder === 'desc' 
        ? b.overallOTR - a.overallOTR 
        : a.overallOTR - b.overallOTR;
    });

  // Calculate position-specific OTR values if they don't exist
  // This is useful if raw data exists but aggregated values don't
  const calculateMissingOtrValues = () => {
    // Check if we have calculated values already
    if (filteredRoutes.some(route => 
      route.firstStopOTR > 0 || route.middleStopOTR > 0 || route.lastStopOTR > 0)) {
      return filteredRoutes;
    }
    
    console.log('Calculating missing OTR values from raw data...');
    
    // Check if we have parsed data available
    if (!reportData.parsedData || !reportData.parsedData.length) {
      console.log('No parsed data available to calculate OTR values');
      return filteredRoutes;
    }
    
    // Try to calculate from raw trip data
    const routeData = {};
    
    // Group data by route
    reportData.parsedData.forEach(record => {
      if (!record.routeNo) return;
      
      const routeNo = record.routeNo;
      if (!routeData[routeNo]) {
        routeData[routeNo] = {
          first: { total: 0, onTime: 0 },
          middle: { total: 0, onTime: 0 },
          last: { total: 0, onTime: 0 }
        };
      }
      
      // Determine stop position
      let position = 'middle';
      if (record.stopPosition === 'first') position = 'first';
      else if (record.stopPosition === 'last') position = 'last';
      
      // Increment counters
      routeData[routeNo][position].total++;
      if (record.timingStatus === 'on-time') {
        routeData[routeNo][position].onTime++;
      }
    });
    
    // Calculate percentages and update routes
    return filteredRoutes.map(route => {
      const routeStats = routeData[route.route];
      if (!routeStats) return route;
      
      // Calculate OTR percentages for each position
      const updatedRoute = { ...route };
      
      if (routeStats.first.total > 0 && route.firstStopOTR === 0) {
        updatedRoute.firstStopOTR = (routeStats.first.onTime / routeStats.first.total) * 100;
      }
      
      if (routeStats.middle.total > 0 && route.middleStopOTR === 0) {
        updatedRoute.middleStopOTR = (routeStats.middle.onTime / routeStats.middle.total) * 100;
      }
      
      if (routeStats.last.total > 0 && route.lastStopOTR === 0) {
        updatedRoute.lastStopOTR = (routeStats.last.onTime / routeStats.last.total) * 100;
      }
      
      return updatedRoute;
    });
  };
  
  // Apply the calculation if needed
  const processedRoutes = calculateMissingOtrValues();

  // Define table columns
  const columns = [
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      width: 300,
      fixed: 'left',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.routeName && record.routeName !== text && (
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.routeName}</Text>
          )}
        </Space>
      )
    },
    {
      title: 'Overall OTR',
      dataIndex: 'overallOTR',
      key: 'overallOTR',
      sorter: (a, b) => a.overallOTR - b.overallOTR,
      render: value => (
        <Progress 
          percent={parseFloat(value.toFixed(2))} 
          size="small" 
          status={value >= 90 ? 'success' : (value >= 80 ? 'normal' : value >= 70 ? 'active' : 'exception')}
          format={percent => `${percent}%`}
        />
      ),
      width: 150
    },
    {
      title: 'Trips',
      dataIndex: 'totalTrips',
      key: 'totalTrips',
      sorter: (a, b) => a.totalTrips - b.totalTrips,
      width: 100
    },
    {
      title: 'First Stop OTR',
      dataIndex: 'firstStopOTR',
      key: 'firstStopOTR',
      sorter: (a, b) => a.firstStopOTR - b.firstStopOTR,
      render: value => formatPercent(value),
      width: 120
    },
    {
      title: 'Middle Stop OTR',
      dataIndex: 'middleStopOTR',
      key: 'middleStopOTR',
      sorter: (a, b) => a.middleStopOTR - b.middleStopOTR,
      render: value => formatPercent(value),
      width: 120
    },
    {
      title: 'Last Stop OTR',
      dataIndex: 'lastStopOTR',
      key: 'lastStopOTR',
      sorter: (a, b) => a.lastStopOTR - b.lastStopOTR,
      render: value => formatPercent(value),
      width: 120
    },
    {
      title: 'Early %',
      dataIndex: 'earlyPercentage',
      key: 'earlyPercentage',
      sorter: (a, b) => a.earlyPercentage - b.earlyPercentage,
      render: value => (
        <Tag color={value < 10 ? 'green' : value < 20 ? 'blue' : 'geekblue'}>
          {formatPercent(value)}
        </Tag>
      ),
      width: 100
    },
    {
      title: 'Late %',
      dataIndex: 'latePercentage',
      key: 'latePercentage',
      sorter: (a, b) => a.latePercentage - b.latePercentage,
      render: value => (
        <Tag color={value < 10 ? 'green' : value < 20 ? 'orange' : 'red'}>
          {formatPercent(value)}
        </Tag>
      ),
      width: 100
    },
    {
      title: 'Very Late %',
      dataIndex: 'veryLatePercentage',
      key: 'veryLatePercentage',
      sorter: (a, b) => a.veryLatePercentage - b.veryLatePercentage,
      render: value => (
        <Tag color={value < 5 ? 'green' : value < 10 ? 'orange' : 'red'}>
          {formatPercent(value)}
        </Tag>
      ),
      width: 100
    },
    {
      title: 'Avg Variance',
      dataIndex: 'averageVariance',
      key: 'averageVariance',
      sorter: (a, b) => a.averageVariance - b.averageVariance,
      render: value => {
        const color = value < 0 ? 'blue' : value <= 60 ? 'green' : value <= 180 ? 'orange' : 'red';
        const plusSign = value > 0 ? '+' : '';
        return (
          <Tag color={color}>
            {plusSign}{value}s
          </Tag>
        );
      },
      width: 120
    },
    {
      title: 'Total Stops',
      dataIndex: 'totalStops',
      key: 'totalStops',
      sorter: (a, b) => a.totalStops - b.totalStops,
      width: 120
    }
  ];

  // Summary statistics
  const summaryStats = {
    totalRoutes: processedRoutes.length,
    avgOTR: processedRoutes.length > 0 
      ? processedRoutes.reduce((sum, route) => sum + route.overallOTR, 0) / processedRoutes.length 
      : 0,
    totalTrips: processedRoutes.reduce((sum, route) => sum + route.totalTrips, 0),
    routesAbove80: processedRoutes.filter(route => route.overallOTR >= 80).length,
    routesBelow70: processedRoutes.filter(route => route.overallOTR < 70).length
  };

  // Toggle sort order function
  const toggleSortOrder = () => {
    setSortOrder(prevOrder => prevOrder === 'desc' ? 'asc' : 'desc');
  };

  return (
    <div className="route-performance">
      <Card 
        title={<Title level={4}>Route Performance Analysis</Title>}
        className="performance-card"
        extra={
          <Space size="middle">
            <Input
              placeholder="Search routes"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
            <Select 
              placeholder="Min trips" 
              style={{ width: 120 }}
              value={minTrips > 0 ? minTrips : undefined}
              onChange={value => setMinTrips(value)}
              allowClear
            >
              <Option value={1}>1+ trips</Option>
              <Option value={5}>5+ trips</Option>
              <Option value={10}>10+ trips</Option>
              <Option value={25}>25+ trips</Option>
              <Option value={50}>50+ trips</Option>
            </Select>
            <Button 
              type="default"
              icon={sortOrder === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
              onClick={toggleSortOrder}
              title={`Sort ${sortOrder === 'desc' ? 'descending' : 'ascending'} by OTR`}
            >
              {sortOrder === 'desc' ? 'Highest OTR' : 'Lowest OTR'}
            </Button>
          </Space>
        }
      >
        {/* Summary statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Card size="small">
              <Statistic 
                title="Total Routes" 
                value={summaryStats.totalRoutes} 
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small">
              <Statistic 
                title="Average OTR" 
                value={formatPercent(summaryStats.avgOTR)}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small">
              <Statistic 
                title="Total Trips" 
                value={summaryStats.totalTrips}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small">
              <Statistic 
                title={<span>Routes <span style={{ color: 'green' }}>≥80%</span></span>}
                value={`${summaryStats.routesAbove80}/${summaryStats.totalRoutes}`}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small">
              <Statistic 
                title={<span>Routes <span style={{ color: 'red' }}>&lt;70%</span></span>}
                value={`${summaryStats.routesBelow70}/${summaryStats.totalRoutes}`}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
        </Row>

        {/* Routes Table */}
        <Table
          dataSource={processedRoutes.map((route, index) => ({ ...route, key: `route-${index}` }))}
          columns={columns}
          pagination={{
            pageSize: tablePageSize,
            defaultPageSize: tablePageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '30', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} routes`,
            onShowSizeChange: (current, size) => setTablePageSize(size)
          }}
          scroll={{ x: 'max-content' }}
          size="small"
          bordered
        />
      </Card>
    </div>
  );
};

export default RoutePerformance; 